import { useState } from "react";
import { PlayCircle, Info, CheckCircle2, X, ChevronDown, ChevronUp } from "lucide-react";

// Convert any YouTube URL (watch?v=, youtu.be, search_query=...) to an embed URL.
// If it's a search results URL or not parseable, return null (we'll link out instead).
function youtubeEmbed(url) {
    if (!url) return null;
    try {
        const u = new URL(url);
        if (u.hostname.includes("youtu.be")) {
            const id = u.pathname.slice(1);
            return id ? `https://www.youtube.com/embed/${id}?autoplay=1&rel=0` : null;
        }
        if (u.hostname.includes("youtube.com")) {
            const v = u.searchParams.get("v");
            if (v) return `https://www.youtube.com/embed/${v}?autoplay=1&rel=0`;
        }
        return null;
    } catch { return null; }
}

function DemoModal({ exercise, onClose }) {
    if (!exercise) return null;
    const embed = youtubeEmbed(exercise.video_url);
    return (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="demo-modal">
            <div className="w-full max-w-3xl" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <div className="text-white">
                        <p className="label-tiny mb-1 text-[var(--gold)]">/ Demo</p>
                        <h3 className="font-serif text-3xl">{exercise.name}</h3>
                    </div>
                    <button onClick={onClose} className="text-white/70 hover:text-white"><X size={22} /></button>
                </div>
                {embed ? (
                    <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
                        <iframe
                            className="absolute inset-0 w-full h-full border border-[var(--gold-deep)]"
                            src={embed}
                            title={`${exercise.name} demo`}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        />
                    </div>
                ) : (
                    <div className="border border-[var(--gold-deep)] bg-[var(--surface)] p-6 text-center">
                        <p className="text-sm text-[var(--text-muted)] mb-4">Your coach hasn't set a direct demo link yet — opening a YouTube search.</p>
                        {exercise.video_url && (
                            <a href={exercise.video_url} target="_blank" rel="noreferrer" className="btn-primary inline-flex items-center gap-2">
                                Open in YouTube ↗
                            </a>
                        )}
                    </div>
                )}
                {exercise.description && (
                    <div className="mt-4 border-l-2 border-[var(--gold)] pl-4 text-sm text-white/80" data-testid="demo-description">
                        {exercise.description}
                    </div>
                )}
            </div>
        </div>
    );
}

export default function WorkoutDetailCard({ workout, onComplete }) {
    const [open, setOpen] = useState(false);
    const [demo, setDemo] = useState(null);

    const exercises = workout.exercises || [];
    const hasExercises = exercises.length > 0;

    return (
        <div className="border-b border-[var(--border)] last:border-b-0" data-testid={`workout-card-${workout.id}`}>
            <div className="p-5 flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                    <p className="font-medium text-xl">{workout.title}</p>
                    <p className="text-xs text-[var(--text-muted)] font-mono mt-1">
                        {(workout.time || "—")} · {(workout.duration_min || exercises.length * 4) || ""}m · {workout.type || "workout"}
                        {hasExercises ? ` · ${exercises.length} exercises` : ""}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    {hasExercises && (
                        <button onClick={() => setOpen(!open)} className="btn-ghost text-xs inline-flex items-center gap-1" data-testid={`toggle-workout-${workout.id}`}>
                            {open ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                            {open ? "Hide" : "View exercises"}
                        </button>
                    )}
                    {workout.completed ? (
                        <span className="tag-pill border-[var(--gold-bright)] text-[var(--gold-bright)]">DONE</span>
                    ) : (
                        <button onClick={() => onComplete(workout.id)} className="btn-primary inline-flex items-center gap-2 text-sm" data-testid={`complete-btn-${workout.id}`}>
                            <CheckCircle2 size={14} /> Mark complete
                        </button>
                    )}
                </div>
            </div>

            {open && hasExercises && (
                <div className="px-5 pb-5 space-y-2" data-testid={`exercise-list-${workout.id}`}>
                    {exercises.map((ex, i) => (
                        <div key={i} className="border border-[var(--border)] bg-[var(--bg-deep)] p-4 flex items-start gap-3" data-testid={`exercise-${workout.id}-${i}`}>
                            <div className="w-8 h-8 flex items-center justify-center border border-[var(--gold-deep)] text-[var(--gold-bright)] font-mono text-xs shrink-0">
                                {String(i + 1).padStart(2, "0")}
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="font-medium">{ex.name}</p>
                                <p className="text-xs text-[var(--text-muted)] font-mono mt-1">
                                    {ex.sets && <>{ex.sets} × {ex.reps || "—"}</>}
                                    {ex.rest && <> · rest {ex.rest}</>}
                                </p>
                                {ex.notes && <p className="text-xs text-[var(--text-muted)] mt-2 italic">{ex.notes}</p>}
                                {ex.description && (
                                    <div className="mt-2 border-l-2 border-[var(--gold-deep)] pl-3 text-xs text-[var(--text-muted)]">
                                        {ex.description}
                                    </div>
                                )}
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                                {(ex.video_url || ex.description) && (
                                    <button
                                        onClick={() => setDemo(ex)}
                                        className="inline-flex items-center gap-1 text-[var(--gold)] hover:text-[var(--gold-bright)] text-xs font-mono tracking-wider uppercase"
                                        data-testid={`watch-demo-${workout.id}-${i}`}
                                        title="Watch demo"
                                    >
                                        <PlayCircle size={16} /> Demo
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <DemoModal exercise={demo} onClose={() => setDemo(null)} />
        </div>
    );
}
